import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  TableSortLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Email as EmailIcon,
  Drafts as DraftsIcon,
  ContactMail as ContactMailIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { contactEnquiriesAPI } from '../../services/api';

const ContactEnquiries = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('ContactEnquiries', 'view');
  const canUpdate = hasPermission('ContactEnquiries', 'update');
  const canDelete = hasPermission('ContactEnquiries', 'delete');
  const canExport = hasPermission('ContactEnquiries', 'export');

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enquiries, setEnquiries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0 });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Detail dialog state
  const [detailDialog, setDetailDialog] = useState({ open: false, enquiry: null });
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, enquiryId: null });

  // Format date in DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  // Load enquiries data
  const loadEnquiries = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        search: searchTerm,
        status: statusFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        date_from: dateFrom,
        date_to: dateTo
      };

      const response = await contactEnquiriesAPI.getAll(params);
      setEnquiries(response.data.enquiries || []);
      setTotalCount(response.data.total || 0);
      setStats(response.data.stats || { total: 0, new: 0, read: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contact enquiries');
      console.error('Error loading contact enquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  // Search with current filters
  const searchEnquiries = async () => {
    setSearchLoading(true);
    setPage(0);
    try {
      setError('');

      const params = {
        page: 1,
        per_page: rowsPerPage,
        search: searchTerm,
        status: statusFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        date_from: dateFrom,
        date_to: dateTo
      };

      const response = await contactEnquiriesAPI.getAll(params);
      setEnquiries(response.data.enquiries || []);
      setTotalCount(response.data.total || 0);
      setStats(response.data.stats || { total: 0, new: 0, read: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search contact enquiries');
      console.error('Error searching contact enquiries:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Mark as read
  const markAsRead = async (enquiryId) => {
    try {
      await contactEnquiriesAPI.updateStatus(enquiryId, { status: 'read' });
      loadEnquiries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
      console.error('Error updating enquiry status:', err);
    }
  };

  // Mark as unread
  const markAsUnread = async (enquiryId) => {
    try {
      await contactEnquiriesAPI.updateStatus(enquiryId, { status: 'new' });
      loadEnquiries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status to unread');
      console.error('Error updating enquiry status:', err);
    }
  };

  // Trigger delete dialog
  const triggerDelete = (enquiryId) => {
    setDeleteDialog({ open: true, enquiryId });
  };

  // Confirm delete enquiry
  const confirmDelete = async () => {
    if (!deleteDialog.enquiryId) return;
    try {
      await contactEnquiriesAPI.delete(deleteDialog.enquiryId);
      loadEnquiries(); // Refresh list after deletion
      if (detailDialog.open && detailDialog.enquiry?.id === deleteDialog.enquiryId) {
        closeDetail(); // Close dialog if the deleted item was open
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete enquiry');
      console.error('Error deleting enquiry:', err);
    } finally {
      setDeleteDialog({ open: false, enquiryId: null });
    }
  };

  // Cancel delete dialog
  const cancelDelete = () => {
    setDeleteDialog({ open: false, enquiryId: null });
  };

  // Handle Export
  const handleExport = async () => {
    if (!canExport) {
      setError('You do not have permission to export');
      return;
    }
    setExportLoading(true);
    setError('');

    try {
      const exportParams = {
        search: searchTerm,
        status: statusFilter,
        date_from: dateFrom,
        date_to: dateTo,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      const response = await contactEnquiriesAPI.exportData(exportParams);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contact_enquiries.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export data:', err);
      setError('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  // Handle search input
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle Enter key in search
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      searchEnquiries();
    }
  };

  // Open detail dialog
  const openDetail = (enquiry) => {
    const activeEnquiry = { ...enquiry };
    // Auto-mark as read when opening
    if (activeEnquiry.status === 'new' && canUpdate) {
      activeEnquiry.status = 'read';
      markAsRead(activeEnquiry.id);
    }
    setDetailDialog({ open: true, enquiry: activeEnquiry });
  };

  // Close detail dialog
  const closeDetail = () => {
    setDetailDialog({ open: false, enquiry: null });
  };

  // Truncate text
  const truncateText = (text, maxLength = 80) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Load data on mount and when dependencies change
  useEffect(() => {
    if (canView) {
      loadEnquiries();
    }
  }, [page, rowsPerPage, sortBy, sortOrder, canView]);

  // Permission check
  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to view the Contact Enquiries module.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Contact Enquiries
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage contact form submissions from the website
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ContactMailIcon color="primary" />
                <Typography color="text.secondary" gutterBottom sx={{ mb: 0 }}>
                  Total Enquiries
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ mt: 1 }}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: '#1976d2' }} />
                <Typography color="text.secondary" gutterBottom sx={{ mb: 0 }}>
                  New
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ mt: 1, color: '#1976d2' }}>
                {stats.new}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DraftsIcon sx={{ color: '#2e7d32' }} />
                <Typography color="text.secondary" gutterBottom sx={{ mb: 0 }}>
                  Read
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ mt: 1, color: '#2e7d32' }}>
                {stats.read}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Action Buttons Row */}
        <Box display="flex" gap={2} justifyContent="flex-end" mb={3}>
          {canExport && (
            <Tooltip title="Export contact enquiries based on applied filters">
              <Button
                variant="outlined"
                onClick={handleExport}
                startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                disabled={exportLoading}
                sx={{
                  height: '40px',
                  px: 3,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Export
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            onClick={loadEnquiries}
            startIcon={<RefreshIcon />}
            sx={{
              height: '40px',
              px: 3,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Search and Filter Row */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search name, email, mobile, message..."
              value={searchTerm}
              onChange={handleSearch}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth variant="outlined" size="medium">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ height: '48px' }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="read">Read</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                }
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={searchEnquiries}
                startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                disabled={searchLoading}
                size="large"
                sx={{
                  height: '48px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  flex: 1
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Sr No.</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'email'}
                    direction={sortBy === 'email' ? sortOrder : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'mobile'}
                    direction={sortBy === 'mobile' ? sortOrder : 'asc'}
                    onClick={() => handleSort('mobile')}
                  >
                    Mobile
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'created_at'}
                    direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                {(canUpdate || canDelete) && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={(canUpdate || canDelete) ? 8 : 7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading enquiries...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : enquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(canUpdate || canDelete) ? 8 : 7} align="center" sx={{ py: 5 }}>
                    <ContactMailIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No contact enquiries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                enquiries.map((enquiry, index) => (
                  <TableRow
                    key={enquiry.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: enquiry.status === 'new' ? 'rgba(25, 118, 210, 0.04)' : 'inherit',
                      '&:hover': {
                        backgroundColor: enquiry.status === 'new' ? 'rgba(25, 118, 210, 0.08)' : undefined
                      }
                    }}
                    onClick={() => openDetail(enquiry)}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: enquiry.status === 'new' ? 600 : 400 }}
                      >
                        {enquiry.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {enquiry.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {enquiry.mobile_code && enquiry.mobile ? `${enquiry.mobile_code} ${enquiry.mobile}` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 300
                        }}
                      >
                        {truncateText(enquiry.message)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={enquiry.status === 'new' ? 'New' : 'Read'}
                        color={enquiry.status === 'new' ? 'primary' : 'success'}
                        size="small"
                        icon={enquiry.status === 'new' ? <EmailIcon /> : <DraftsIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(enquiry.created_at)}
                      </Typography>
                    </TableCell>
                    {(canUpdate || canDelete) && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {canUpdate && enquiry.status === 'new' && (
                            <Tooltip title="Mark as Read">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => markAsRead(enquiry.id)}
                              >
                                <MarkEmailReadIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canUpdate && enquiry.status === 'read' && (
                            <Tooltip title="Mark as Unread">
                              <IconButton
                                size="small"
                                sx={{ color: 'text.secondary' }}
                                onClick={() => markAsUnread(enquiry.id)}
                              >
                                <MarkEmailUnreadIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => triggerDelete(enquiry.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={closeDetail}
        maxWidth="md"
        fullWidth
      >
        {detailDialog.enquiry && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ContactMailIcon color="primary" />
                <Typography variant="h6">Enquiry Details</Typography>
              </Box>
              <IconButton onClick={closeDetail} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Mobile</Typography>
                  <Typography variant="body1">
                    {detailDialog.enquiry.mobile_code && detailDialog.enquiry.mobile ? `${detailDialog.enquiry.mobile_code} ${detailDialog.enquiry.mobile}` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {detailDialog.enquiry.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">
                    <a href={`mailto:${detailDialog.enquiry.email}`} style={{ color: '#1976d2', textDecoration: 'none' }}>
                      {detailDialog.enquiry.email}
                    </a>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={detailDialog.enquiry.status === 'new' ? 'New' : 'Read'}
                    color={detailDialog.enquiry.status === 'new' ? 'primary' : 'success'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Submitted On</Typography>
                  <Typography variant="body1">
                    {formatDateTime(detailDialog.enquiry.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Message</Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      borderLeft: '4px solid #1976d2',
                      maxHeight: 300,
                      overflowY: 'auto'
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detailDialog.enquiry.message}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Box sx={{ flex: 1 }}>
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => triggerDelete(detailDialog.enquiry.id)}
                  >
                    Delete
                  </Button>
                )}
              </Box>
              {canUpdate && detailDialog.enquiry.status === 'new' && (
                <Button
                  variant="outlined"
                  startIcon={<MarkEmailReadIcon />}
                  onClick={() => {
                    markAsRead(detailDialog.enquiry.id);
                    closeDetail();
                  }}
                >
                  Mark as Read
                </Button>
              )}
              {canUpdate && detailDialog.enquiry.status === 'read' && (
                <Button
                  variant="outlined"
                  sx={{ color: 'text.secondary', borderColor: 'text.disabled' }}
                  startIcon={<MarkEmailUnreadIcon />}
                  onClick={() => {
                    markAsUnread(detailDialog.enquiry.id);
                    closeDetail();
                  }}
                >
                  Mark as Unread
                </Button>
              )}
              <Button variant="contained" onClick={closeDetail} sx={{ ml: 1 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6">Confirm Deletion</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this contact enquiry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactEnquiries;
